import { z } from "zod";
import { queryOptions, type QueryClient, type UseMutationOptions, type UseQueryOptions } from "@tanstack/react-query";

import { api, ApiOptions, mBaseURL } from "@/config/api";
import { unwrapApiError } from "@/lib/error";
import { APIResponseSuccessSchema } from "@/schema/api";

type QueryOptions<T> = Omit<UseQueryOptions<T>, "queryKey" | "queryFn">;

const CreateRuleRequestSchema = z.object({
    trigger: z.string(),
    trigger_params: z.array(z.string()),
    platform: z.array(z.string()),
    perform: z.string(),
    perform_confidence_score: z.number(),
    actions: z.array(z.string()),
});

const RuleSchema = z.object({
    id: z.string(),
    trigger: z.string(),
    trigger_params: z.array(z.string()),
    platform: z.array(z.string()),
    perform: z.string(),
    perform_confidence_score: z.number(),
    actions: z.array(z.string()).nullable(),
    created_at: z.string().nullable(),
    updated_at: z.string().nullable(),
    deleted_at: z.string().nullable(),
}).passthrough();

const CreateRuleResponseSchema = APIResponseSuccessSchema.extend({
    data: z.unknown().optional(),
}).passthrough();

const GetRulesListResponseSchema = APIResponseSuccessSchema.extend({
    data: z
        .object({
            rules: z.array(RuleSchema),
        })
        .optional(),
}).passthrough();

const GetRuleByIdResponseSchema = APIResponseSuccessSchema.extend({
    data: RuleSchema.optional(),
}).passthrough();

const UpdateRuleByIdRequestSchema = CreateRuleRequestSchema;

const UpdateRuleByIdResponseSchema = APIResponseSuccessSchema.extend({
    data: z.unknown().optional(),
}).passthrough();

export type UpdateRuleByIdResponse = z.infer<typeof UpdateRuleByIdResponseSchema>;

const DeleteRuleByIdResponseSchema = APIResponseSuccessSchema.extend({
    data: z.unknown().optional(),
}).passthrough();

export type DeleteRuleByIdResponse = z.infer<typeof DeleteRuleByIdResponseSchema>;

/** GET /api/brand/moderation/ai_suggestions — AI topic suggestions for rule builder (data shape varies; success envelope only) */
const AiTopicSuggestionsResponseSchema = APIResponseSuccessSchema.extend({
    data: z.array(z.string()).optional(),
}).passthrough();

export type AiTopicSuggestionsResponse = z.infer<typeof AiTopicSuggestionsResponseSchema>;

export const RulesApiFactory = {
    Endpoint: mBaseURL + "/api/brand/moderation/rules",
    AiTopicSuggestionsEndpoint: mBaseURL + "/api/brand/moderation/ai_suggestions",
    Keys: {
        GetRulesList: ["brand-moderation", "rules", "list"] as const,
        GetRuleById: (id: string | number) => ["brand-moderation", "rules", "by-id", String(id)] as const,
        AiTopicSuggestions: ["brand-moderation", "rules", "ai-topic-suggestions"] as const,
    },

    Invalidations: {
        AfterUpdateRuleById: async (queryClient: QueryClient, id: string | number) => {
            await queryClient.invalidateQueries({ queryKey: RulesApiFactory.Keys.GetRulesList });
            await queryClient.invalidateQueries({ queryKey: RulesApiFactory.Keys.GetRuleById(id) });
        },
        AfterDeleteRuleById: async (queryClient: QueryClient, id: string | number) => {
            await queryClient.invalidateQueries({ queryKey: RulesApiFactory.Keys.GetRulesList });
            await queryClient.invalidateQueries({ queryKey: RulesApiFactory.Keys.GetRuleById(id) });
        },
    },

    Schemas: {
        Request: {
            CreateRule: CreateRuleRequestSchema,
            UpdateRuleById: UpdateRuleByIdRequestSchema,
        },

        Response: {
            CreateRule: CreateRuleResponseSchema,
            GetRulesList: GetRulesListResponseSchema,
            GetRuleById: GetRuleByIdResponseSchema,
            UpdateRuleById: UpdateRuleByIdResponseSchema,
            DeleteRuleById: DeleteRuleByIdResponseSchema,
            AiTopicSuggestions: AiTopicSuggestionsResponseSchema,
        },
    },

    Apis: {
        GetRulesList: async (options?: ApiOptions) => {
            const response = await api(RulesApiFactory.Endpoint, {
                method: "GET",
                ...(options || {}),
            });

            if (!response.ok) {
                const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
                throw new Error(unwrapApiError(error));
            }

            return response.json().then(GetRulesListResponseSchema.parse);
        },
        GetRuleById: async (id: string | number, options?: ApiOptions) => {
            const response = await api(`${RulesApiFactory.Endpoint}/${id}`, {
                method: "GET",
                ...(options || {}),
            });

            if (!response.ok) {
                const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
                throw new Error(unwrapApiError(error));
            }

            return response.json().then(GetRuleByIdResponseSchema.parse);
        },
        UpdateRuleById: async (id: string | number, body: z.infer<typeof UpdateRuleByIdRequestSchema>, options?: ApiOptions) => {
            const response = await api(`${RulesApiFactory.Endpoint}/${id}`, {
                method: "PATCH",
                body: JSON.stringify(body),
                ...(options || {}),
            });

            if (!response.ok) {
                const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
                throw new Error(unwrapApiError(error));
            }

            return response.json().then(UpdateRuleByIdResponseSchema.parse);
        },
        DeleteRuleById: async (id: string | number, options?: ApiOptions) => {
            const response = await api(`${RulesApiFactory.Endpoint}/${id}`, {
                method: "DELETE",
                ...(options || {}),
            });

            if (!response.ok) {
                const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
                throw new Error(unwrapApiError(error));
            }

            return response.json().then(DeleteRuleByIdResponseSchema.parse);
        },
        CreateRule: async (body: z.infer<typeof CreateRuleRequestSchema>, options?: ApiOptions) => {
            const response = await api(RulesApiFactory.Endpoint, {
                method: "POST",
                body: JSON.stringify(body),
                ...(options || {}),
            });

            if (!response.ok) {
                const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
                throw new Error(unwrapApiError(error));
            }

            return response.json().then(CreateRuleResponseSchema.parse);
        },
        /** GET /api/brand/moderation/ai_suggestions */
        AiTopicSuggestions: async (options?: ApiOptions) => {
            const response = await api(RulesApiFactory.AiTopicSuggestionsEndpoint, {
                method: "GET",
                ...(options || {}),
            });

            if (!response.ok) {
                const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
                throw new Error(unwrapApiError(error));
            }

            return response.json().then(RulesApiFactory.Schemas.Response.AiTopicSuggestions.parse);
        },
    },
    Queries: {
        GetRulesList: (options?: QueryOptions<z.infer<typeof GetRulesListResponseSchema>>) =>
            queryOptions({
                queryKey: RulesApiFactory.Keys.GetRulesList,
                queryFn: () => RulesApiFactory.Apis.GetRulesList(),
                ...options,
            }),
        GetRuleById: (id: string | number, options?: QueryOptions<z.infer<typeof GetRuleByIdResponseSchema>>) =>
            queryOptions({
                queryKey: RulesApiFactory.Keys.GetRuleById(id),
                queryFn: () => RulesApiFactory.Apis.GetRuleById(id),
                ...options,
            }),
        AiTopicSuggestions: (options?: QueryOptions<AiTopicSuggestionsResponse>) =>
            queryOptions({
                queryKey: RulesApiFactory.Keys.AiTopicSuggestions,
                queryFn: () => RulesApiFactory.Apis.AiTopicSuggestions(),
                ...options,
            }),
    },

    Mutations: {
        updateRuleByIdOptions: (
            queryClient: QueryClient,
        ): UseMutationOptions<
            UpdateRuleByIdResponse,
            Error,
            { id: string | number; body: z.infer<typeof UpdateRuleByIdRequestSchema> }
        > => ({
            mutationFn: ({ id, body }) => RulesApiFactory.Apis.UpdateRuleById(id, body),
            onSuccess: async (_data, { id }) => {
                await RulesApiFactory.Invalidations.AfterUpdateRuleById(queryClient, id);
            },
        }),
        deleteRuleByIdOptions: (queryClient: QueryClient): UseMutationOptions<DeleteRuleByIdResponse, Error, string | number> => ({
            mutationFn: (ruleId) => RulesApiFactory.Apis.DeleteRuleById(ruleId),
            onSuccess: async (_data, ruleId) => {
                await RulesApiFactory.Invalidations.AfterDeleteRuleById(queryClient, ruleId);
            },
        }),
    },
};

export type Rule = z.infer<typeof RuleSchema>;

export type CreateRuleRequest = z.infer<typeof CreateRuleRequestSchema>;
export type CreateRuleResponse = z.infer<typeof CreateRuleResponseSchema>;
export type GetRulesListResponse = z.infer<typeof GetRulesListResponseSchema>;
export type GetRuleByIdResponse = z.infer<typeof GetRuleByIdResponseSchema>;
export type UpdateRuleByIdRequest = z.infer<typeof UpdateRuleByIdRequestSchema>;
